import { average } from '../../../src/features/cron-calculator/cron-calculator-math.js';
import { el, fmt } from './dom.js';

const SVG = 'http://www.w3.org/2000/svg';
const MARGIN = { top: 16, right: 16, bottom: 30, left: 44 };
const HEIGHT = 280;

function svg(tag, attributes = {}) {
  const node = document.createElementNS(SVG, tag);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, value);
  return node;
}

function niceMax(value) {
  if (!(value > 0)) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return [1, 2, 2.5, 5, 10].map((step) => step * magnitude).find((candidate) => candidate >= value) || value;
}

const dayMs = 24 * 60 * 60 * 1000;
const toTime = (date) => Date.parse(`${date}T00:00:00Z`);

// Cron/Pérola ao longo do tempo: um ponto por registro e a média mensal.
export function renderCronHistoryChart(host, points) {
  host.replaceChildren();
  if (!points.length) {
    host.append(el('p', { className: 'mc-empty', text: 'Ainda não há registros com Crons neste período.' }));
    return;
  }
  const width = Math.max(host.clientWidth || 640, 300);
  const innerWidth = width - MARGIN.left - MARGIN.right;
  const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom;
  const times = points.map((point) => toTime(point.purchasedAt));
  let minTime = Math.min(...times);
  let maxTime = Math.max(...times);
  if (minTime === maxTime) { minTime -= 15 * dayMs; maxTime += 15 * dayMs; }
  const yMax = niceMax(Math.max(...points.map((point) => point.cronPerPearl)) * 1.1);
  const x = (time) => MARGIN.left + ((time - minTime) / (maxTime - minTime)) * innerWidth;
  const y = (value) => MARGIN.top + innerHeight - (value / yMax) * innerHeight;

  const chart = svg('svg', { viewBox: `0 0 ${width} ${HEIGHT}`, width: String(width), height: String(HEIGHT), role: 'img', 'aria-label': 'Cron por Pérola ao longo do tempo' });
  for (let index = 0; index <= 4; index += 1) {
    const value = (yMax / 4) * index;
    chart.append(svg('line', { x1: MARGIN.left, x2: width - MARGIN.right, y1: y(value), y2: y(value), class: 'mc-chart-grid' }));
    const label = svg('text', { x: MARGIN.left - 8, y: y(value) + 4, 'text-anchor': 'end', class: 'mc-chart-axis' });
    label.textContent = fmt.cpp(value);
    chart.append(label);
  }
  const tickCount = Math.min(5, Math.max(2, Math.floor(innerWidth / 130)));
  for (let index = 0; index <= tickCount; index += 1) {
    const time = minTime + ((maxTime - minTime) / tickCount) * index;
    const label = svg('text', { x: x(time), y: HEIGHT - 8, 'text-anchor': index === 0 ? 'start' : index === tickCount ? 'end' : 'middle', class: 'mc-chart-axis' });
    label.textContent = fmt.date(new Date(time).toISOString());
    chart.append(label);
  }

  const months = new Map();
  for (const point of points) {
    const key = point.purchasedAt.slice(0, 7);
    if (!months.has(key)) months.set(key, []);
    months.get(key).push(point.cronPerPearl);
  }
  const monthly = [...months.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, values]) => ({ time: toTime(`${month}-15`), value: average(values) }));
  if (monthly.length > 1) {
    const clamp = (time) => Math.min(Math.max(time, minTime), maxTime);
    chart.append(svg('path', { d: monthly.map((point, index) => `${index ? 'L' : 'M'}${x(clamp(point.time)).toFixed(1)},${y(point.value).toFixed(1)}`).join(' '), class: 'mc-chart-average' }));
  }
  const dots = points.map((point, index) => {
    const dot = svg('circle', { cx: x(times[index]), cy: y(point.cronPerPearl), r: 4, class: 'mc-chart-dot' });
    chart.append(dot);
    return { dot, point, cx: x(times[index]), cy: y(point.cronPerPearl) };
  });

  const tooltip = el('div', { className: 'mc-chart-tooltip', hidden: true });
  let active = null;
  chart.addEventListener('pointermove', (event) => {
    const box = chart.getBoundingClientRect();
    const scale = width / box.width;
    const px = (event.clientX - box.left) * scale;
    const py = (event.clientY - box.top) * scale;
    let nearest = null;
    let distance = Infinity;
    for (const item of dots) {
      const current = Math.hypot(item.cx - px, item.cy - py);
      if (current < distance) { distance = current; nearest = item; }
    }
    active?.dot.classList.remove('is-active');
    if (!nearest || distance > 18) { tooltip.hidden = true; active = null; return; }
    active = nearest;
    nearest.dot.classList.add('is-active');
    tooltip.replaceChildren(el('strong', { text: nearest.point.packageName }), el('span', { text: `${fmt.date(nearest.point.purchasedAt)} · ${fmt.cpp(nearest.point.cronPerPearl)} Cron/Pérola` }));
    tooltip.hidden = false;
    const left = nearest.cx / scale;
    tooltip.style.left = `${Math.min(Math.max(left, 90), box.width - 90)}px`;
    tooltip.style.top = `${nearest.cy / scale - 12}px`;
  });
  chart.addEventListener('pointerleave', () => { tooltip.hidden = true; active?.dot.classList.remove('is-active'); active = null; });

  const legend = el('div', { className: 'mc-chart-legend' }, [
    el('span', {}, [el('i', { className: 'mc-legend-dot', 'aria-hidden': 'true' }), `Registro (${fmt.int(points.length)})`]),
    monthly.length > 1 ? el('span', {}, [el('i', { className: 'mc-legend-line', 'aria-hidden': 'true' }), 'Média mensal']) : null
  ]);
  host.append(legend, el('div', { className: 'mc-chart-frame' }, [chart, tooltip]));
}
